import { useEffect, useMemo, useState } from "react";
import Top from "./Top";
import Footer from "./Footer";
import SLNScanHeader from "./SLNScanHeader";
import { ALL_SYMBOLS } from "../json/companies";
import Select from "react-select";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const API_ALL_LATEST =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetAllLatestOptionChainHistory";

const API_HISTORY =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetOptionChainHistory";

const INDEX_SYMBOLS = [
  { key: "NIFTY50", symbol: "NSE:NIFTY50-INDEX" },
  { key: "BANKNIFTY", symbol: "NSE:NIFTYBANK-INDEX" },
  { key: "FINNIFTY", symbol: "NSE:FINNIFTY-INDEX" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmt2(n) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n)))
    return "—";
  return Number(n).toFixed(2);
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function fmtTimeLabel(ms) {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fmtDateTimeLabel(ms, withDate) {
  const d = new Date(ms);
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (!withDate) return time;

  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
  return `${date} ${time}`;
}

// rangeDays: 1(today), 3, 7, 15, 30
function getMarketRangeMsForDays(rangeDays) {
  const now = new Date();

  // start day = today - (rangeDays-1)
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - (rangeDays - 1),
    9,
    15,
    0,
    0
  );

  const todayClose = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    15,
    30,
    0,
    0
  );

  // if market not closed yet, to = now else to = close
  const to = now.getTime() < todayClose.getTime() ? now : todayClose;

  return { from: start.getTime(), to: to.getTime() };
}

export default function OILiner() {
  const [activeIndex, setActiveIndex] = useState("NIFTY50");
  const [symbol, setSymbol] = useState("NSE:NIFTY50-INDEX");
  const [minutes, setMinutes] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("PCR"); // "PCR" | "VOLCPR"
  const [range, setRange] = useState([0, 0]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState(1); // 1 = Today

  const rangeOptions = useMemo(
    () => [
      { value: 1, label: "Today" },
      { value: 3, label: "Past 3 Days" },
      { value: 7, label: "Past 7 Days" },
      { value: 15, label: "Past 15 Days" },
      { value: 30, label: "Past 30 Days" },
    ],
    []
  );

  const selectedRangeOption = useMemo(
    () => rangeOptions.find((x) => x.value === rangeDays) || rangeOptions[0],
    [rangeOptions, rangeDays]
  );

  useEffect(() => {
    if (rangeDays > 1 && minutes !== "ALL") setMinutes("ALL");
  }, [rangeDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // when click top tabs → set symbol to that index
  useEffect(() => {
    const hit = INDEX_SYMBOLS.find((x) => x.key === activeIndex);
    if (hit) setSymbol(hit.symbol);
  }, [activeIndex]);

  function getMarketRangeMs() {
    const now = new Date();

    const from = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      9,
      15,
      0,
      0
    );

    const close = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      15,
      30,
      0,
      0
    );

    const to = now.getTime() < close.getTime() ? now : close;
    return { from: from.getTime(), to: to.getTime() };
  }

  // ✅ API_HISTORY NOW RETURNS CALCULATED DATA
  async function fetchHistory(sym, interval, days) {
    const token = localStorage.getItem("SLNToken");
    if (!token) throw new Error("Token not found in localStorage.");

    let url = "";

    // ✅ Multi-day or ALL => use from/to
    if (days > 1 || interval === "ALL") {
      const { from, to } = getMarketRangeMsForDays(days);
      url = `${API_HISTORY}?symbol=${encodeURIComponent(
        sym
      )}&strikecount=100&from=${encodeURIComponent(
        from
      )}&to=${encodeURIComponent(to)}`;
    } else {
      // ✅ Today + selected interval minutes
      url = `${API_HISTORY}?symbol=${encodeURIComponent(
        sym
      )}&strikecount=100&minutes=${encodeURIComponent(interval)}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.s === "error") {
      throw new Error(
        json?.message || json?.error || `Request failed (${res.status})`
      );
    }

    return Array.isArray(json?.data) ? json.data : [];
  }

  // ✅ Cards use backend-calculated response: { rows: [...] }
  async function loadCards() {
    try {
      setCardsLoading(true);

      const token = localStorage.getItem("SLNToken");
      if (!token) throw new Error("Token not found in localStorage.");

      const url = `${API_ALL_LATEST}?page=1&limit=200`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: token, "Content-Type": "application/json" },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.s === "error") {
        throw new Error(json?.message || `Failed (${res.status})`);
      }

      const list = Array.isArray(json?.rows) ? json.rows : [];

      const cleaned = list
        .map((r) => ({
          symbol: String(r.symbol || ""),
          short:
            String(r.short || "") ||
            String(r.symbol || "")
              .replace("NSE:", "")
              .replace("-EQ", ""),
          ltp: safeNum(r.ltp),
          chg: safeNum(r.ltpChange), // UI expects c.chg
          chgp: safeNum(r.changePct), // UI expects c.chgp
          optionPCR: safeNum(r.optionPCR),
          volCPR: safeNum(r.volCPR),
        }))
        .filter((r) => r.symbol && r.short);

      setCards(cleaned);
    } catch (e) {
      console.error(e);
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  }

  useEffect(() => {
    loadCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      setErr("");
      setLoading(true);
      const list = await fetchHistory(symbol, minutes, rangeDays);
      setHistory(list);
    } catch (e) {
      setHistory([]);
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, minutes, mode, rangeDays]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "top" },
        tooltip: { enabled: true },
      },
      scales: {
        y: {
          position: "left",
          title: { display: true, text: mode === "PCR" ? "PCR" : "Vol CPR" },
        },
        y1: {
          position: "right",
          grid: { drawOnChartArea: false },
          title: { display: true, text: "LTP" },
        },
        x: {
          title: { display: true, text: "Time" },
        },
      },
    };
  }, [mode]);

  const symbolOptions = useMemo(() => {
    const indexOpts = INDEX_SYMBOLS.map((x) => ({
      value: x.symbol,
      label: `${x.key} (${x.symbol})`,
    }));

    const stockOpts = ALL_SYMBOLS.map((s) => ({
      value: s,
      label: s,
    }));

    return [...indexOpts, ...stockOpts];
  }, []);

  const selectedSymbolOption = useMemo(() => {
    return symbolOptions.find((o) => o.value === symbol) || null;
  }, [symbolOptions, symbol]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 38,
      borderRadius: 8,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const intervalOptions = useMemo(() => {
    return [
      { value: "ALL", label: "All Time (09:15 - Now)" },
      { value: 15, label: "15 min" },
      { value: 30, label: "30 min" },
      { value: 60, label: "60 min" },
    ];
  }, []);

  const selectedIntervalOption = useMemo(() => {
    return (
      intervalOptions.find((o) => o.value === minutes) || intervalOptions[0]
    );
  }, [intervalOptions, minutes]);

  // ✅ Chart now uses backend calculated fields directly (no chain parsing)
  const { points, chartData } = useMemo(() => {
    const pts = history
      .map((it) => {
        const ts =
          safeNum(it?.ts) ??
          safeNum(it?.timestamp) ??
          safeNum(it?.createdAt) ??
          null;

        const pcr = safeNum(it?.optionPCR); // PCR Trend uses optionPCR
        const volCpr = safeNum(it?.volCPR); // Volume CPR uses volCPR
        const ltp = safeNum(it?.ltp);

        return { ts, pcr, volCpr, ltp };
      })
      .filter((p) => p.ts != null)
      .sort((a, b) => a.ts - b.ts);

    const start = Math.max(0, Math.min(range[0], Math.max(0, pts.length - 1)));
    const end = Math.max(
      start,
      Math.min(range[1], Math.max(0, pts.length - 1))
    );
    const sliced = pts.slice(start, end + 1);
    const labels = sliced.map((p) => fmtDateTimeLabel(p.ts, rangeDays > 1));
    const pcrSeries = sliced.map((p) => p.pcr);
    const ltpSeries = sliced.map((p) => p.ltp);
    const volCprSeries = sliced.map((p) => p.volCpr);

    return {
      points: pts,
      chartData: {
        labels,
        datasets: [
          {
            label: mode === "PCR" ? "PCR" : "Vol CPR",
            data: mode === "PCR" ? pcrSeries : volCprSeries,
            tension: 0.35,
            pointRadius: 0,
            borderWidth: 2,
            fill: false,
            borderColor: mode === "PCR" ? "#0d6efd" : "#198754",
            backgroundColor: mode === "PCR" ? "#0d6efd" : "#198754",
            yAxisID: "y",
          },
          {
            label: "LTP",
            data: ltpSeries,
            tension: 0.35,
            pointRadius: 0,
            borderWidth: 2,
            fill: false,
            borderColor: "#dc3545",
            backgroundColor: "#dc3545",
            yAxisID: "y1",
          },
        ],
      },
    };
  }, [history, mode, range, rangeDays]);

  useEffect(() => {
    if (!history?.length) return;
    setRange([0, Math.max(0, history.length - 1)]);
  }, [history]);

  return (
    <>
      <Top />
      <SLNScanHeader page={"oiliner"} />

      <section className="mb-7 mt-4">
        <div className="container">

          <div className="card border-0 shadow-sm bg-light rounded-4 mb-2">
            <div className="card-body p-3 p-md-4">
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-muted mb-1">
                    Symbol
                  </label>
                  <Select
                    value={selectedSymbolOption}
                    onChange={(opt) =>
                      setSymbol(opt?.value || "NSE:NIFTY50-INDEX")
                    }
                    options={symbolOptions}
                    isSearchable
                    placeholder="Search Symbol"
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label small text-muted mb-1">
                    Interval
                  </label>
                  <Select
                    value={selectedIntervalOption}
                    onChange={(opt) => setMinutes(opt?.value ?? "ALL")}
                    options={intervalOptions}
                    isSearchable={false}
                    placeholder="Interval"
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 d-flex justify-content-md-end gap-2">
                  <button
                    className={`btn rounded-3 fw-medium px-4 ${
                      mode === "PCR"
                        ? "btn-dark text-white"
                        : "btn-outline-dark"
                    }`}
                    onClick={() => setMode("PCR")}
                  >
                    PCR Trend
                  </button>
                  <button
                    className={`btn rounded-3 fw-medium px-4 ${
                      mode === "VOLCPR"
                        ? "btn-dark text-white"
                        : "btn-outline-dark"
                    }`}
                    onClick={() => setMode("VOLCPR")}
                  >
                    Volume CPR
                  </button>
                </div>
              </div>

              {err ? (
                <div className="alert alert-danger mt-3 mb-0">
                  <div className="fw-semibold">Error</div>
                  <div className="small">{err}</div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Chart */}
          <div className="card border shadow-sm bg-white rounded-4 mx-lg-6">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold text-dark">Intraday Analysis</div>
                <span className="text-muted small">
                  {loading ? "Loading…" : `${history.length} points`}
                </span>
              </div>

              <div style={{ height: 420 }}>
                <Line data={chartData} options={options} />

                {points.length > 1 ? (
                  <div>
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>
                        {fmtDateTimeLabel(points[range[0]]?.ts, rangeDays > 1)}
                      </span>
                      <span>
                        {fmtDateTimeLabel(points[range[1]]?.ts, rangeDays > 1)}
                      </span>
                    </div>

                    <Slider
                      range
                      min={0}
                      max={points.length - 1}
                      value={range}
                      onChange={(val) => setRange(val)}
                      allowCross={false}
                      trackStyle={[{ backgroundColor: "#0d6efd" }]}
                      handleStyle={[
                        { borderColor: "#0d6efd" },
                        { borderColor: "#0d6efd" },
                      ]}
                      railStyle={{ backgroundColor: "#dee2e6" }}
                    />
                  </div>
                ) : null}
              </div>

              <div className="mt-7">
                <div className="col-12 col-md-3">
                  <Select
                    value={selectedRangeOption}
                    onChange={(opt) => setRangeDays(Number(opt?.value || 1))}
                    options={rangeOptions}
                    isSearchable={false}
                    styles={selectStyles}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ SAME cards UI — only data source changed */}
      <section className="mt-5 bg-light py-7">
        <div className="container">
          <div className="row g-2">
            {cards.map((c) => {
              const down = Number(c.chg) < 0;
              const metric = mode === "PCR" ? c.optionPCR : c.volCPR;

              return (
                <div key={c.symbol} className="col-6 col-md-4 col-xl-2">
                  <div
                    className="card border-0 shadow-sm rounded-4 h-100 bg-white cursor-pointer text-black"
                    onClick={() => setSymbol(c.symbol)}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{c.short}</div>
                          <div className="small text-muted">
                            {mode === "PCR" ? "PCR" : "VOL CPR"}: {fmt2(metric)}
                          </div>
                        </div>

                        <button
                          className="btn btn-sm rounded-circle icon-shape btn-light d-flex justify-content-center align-items-center text-black icon-md rounded-circle"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/option-chain?symbol=${encodeURIComponent(
                                c.symbol
                              )}&strikecount=100`
                            );
                          }}
                          title="View Option Chain"
                        >
                          <i className="bi bi-eye fs-4" />
                        </button>
                      </div>

                      <div className="mt-3">
                        <div className="text-muted small">LTP</div>
                        <div className="fs-4 fw-bold">{fmt2(c.ltp)}</div>
                      </div>

                      <div className="mt-2 d-flex justify-content-between align-items-center">
                        <span className="small text-muted">Change</span>
                        <span
                          className={`badge rounded-pill px-3 ${
                            down
                              ? "bg-danger-soft text-danger"
                              : "bg-success-soft text-success"
                          }`}
                        >
                          {fmt2(c.chgp)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {cardsLoading ? (
            <div className="text-center mt-3">
              <div className="spinner-border" role="status"></div>
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </>
  );
}
